import { RasterImageName } from '~assets/raster/generated';
import { ColDef } from '~components/dataGrids/interfaces/colDef';
import { CommandTypeEnum, DeviceTypeEnum, HardwareProviderEnum } from '~enums';
import { BaseReference } from '~interfaces';

import DeviceLog, { deviceLogGridCols } from './deviceLog';

interface DeviceModel extends BaseReference {
	/**
	 * Name of the model, can be one of the below mentioned list (This is a dynamic list from the backend so it can change):
	 * UNDEFINED, FMC130, FMC130_PRO, FMC125, FMC125_PRO, FMC003, SKBIEL01, SKBIEL01_PRO, SKBIEL01_PRO_KEYPAD, SKBIEL03_PRO, SKBIEL03, SKTREL02, SKTREL03_PRO, SKTREL03, SKDOEL01, SKDOEL02, SKDOEL03, SKDOEL04, SKTN01, SKBR01, SKBR02, SKBR02_CTRL
	 */
	name: string;
	resourceGroupId?: number;
	description?: string;
	deviceType?: DeviceTypeEnum;
	hardwareProvider?: HardwareProviderEnum;
	commandTypes?: CommandTypeEnum[];
}

export const getCommandsForDeviceModel = (model: DeviceModel): CommandTypeEnum[] => {
	switch (model.name) {
		case 'SKBIEL01':
			return [CommandTypeEnum.Unlock, CommandTypeEnum.Reboot, CommandTypeEnum.HardwareReset];
		case 'SKBIEL01_PRO':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.Synchronise,
				CommandTypeEnum.TriggerGps,
				CommandTypeEnum.HardwareReset,
			];
		case 'SKBIEL01_PRO_KEYPAD':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.Synchronise,
				CommandTypeEnum.TriggerGps,
				CommandTypeEnum.HardwareReset,
			];
		case 'SKBIEL03':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.BatteryLock,
				CommandTypeEnum.BatteryUnlock,
			];
		case 'SKBIEL03_PRO':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.Synchronise,
				CommandTypeEnum.TriggerGps,
				CommandTypeEnum.BatteryLock,
				CommandTypeEnum.BatteryUnlock,
			];
		case 'SKDOEL01':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.EnableOfficeMode,
				CommandTypeEnum.DisableOfficeMode,
			];
		case 'SKDOEL02':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.EnableOfficeMode,
				CommandTypeEnum.DisableOfficeMode,
			];
		case 'SKDOEL03':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				// CommandTypeEnum.EnableOfficeMode, Will be added somewhere in the future.
				// CommandTypeEnum.DisableOfficeMode,
			];
		case 'SKDOEL04':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.EnableOfficeMode,
				CommandTypeEnum.DisableOfficeMode,
			];
		case 'SKTREL02':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.TriggerGps,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.Synchronise,
			];
		case 'SKTREL03':
			return [CommandTypeEnum.Unlock, CommandTypeEnum.Reboot];
		case 'SKTREL03_PRO':
			return [
				CommandTypeEnum.Unlock,
				CommandTypeEnum.TriggerGps,
				CommandTypeEnum.Reboot,
				CommandTypeEnum.Synchronise,
			];
	}
	return [];
};

export const deviceModelImageNames: Record<string, RasterImageName> = {
	FMC125: 'device/model/fmc-125-130',
	FMC130: 'device/model/fmc-125-130',
	FMC125_PRO: 'device/model/fmc-125-130',
	FMC130_PRO: 'device/model/fmc-125-130',
	SKBIEL03: 'device/model/skbiel-03',
	SKBIEL03_PRO: 'device/model/skbiel-03',
	SKBR01: 'device/model/skbr-ctrl',
	SKBR02: 'device/model/skbr-ctrl',
	SKBR02_CTRL: 'device/model/skbr-ctrl',
	SKBR03: 'device/model/skbr-ctrl',
	SKDOEL01: 'device/model/skdoel-01',
	SKDOEL02: 'device/model/skdoel-02',
	SKDOEL03: 'device/model/skdoel-03',
	SKTREL02: 'device/model/sktrel',
	SKTREL03_PRO: 'device/model/sktrel',
	SKTREL03: 'device/model/sktrel',
};

export const getHeartbeatDataColDefs = (model?: DeviceModel): ColDef<DeviceLog>[] => {
	switch (model?.name) {
		case 'FMC130':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.deviceTimestamp,
				deviceLogGridCols.serverTimestamp,
				deviceLogGridCols.lockState,
				deviceLogGridCols.isIgnitionOn,
				deviceLogGridCols.ioDigitalIn1,
				deviceLogGridCols.ioDigitalIn2,
				deviceLogGridCols.ioDigitalOut1,
				deviceLogGridCols.speedKiloMeters,
				deviceLogGridCols.ioSpeedKiloMetersPerHour,
				deviceLogGridCols.ioMileageInMeters,
				deviceLogGridCols.ioIbuttonCode,
				deviceLogGridCols.ioBatteryPercentage,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.chargingStatus,
				deviceLogGridCols.avlUndefined,
				deviceLogGridCols.canBus1,
				deviceLogGridCols.canBus2,
				deviceLogGridCols.canBus3,
				deviceLogGridCols.fuelLevelPercentage,
				deviceLogGridCols.canProgram,
				deviceLogGridCols.canSecurityStateFlags,
				deviceLogGridCols.batteryRangeMeters,
				deviceLogGridCols.clientId,
				deviceLogGridCols.codecId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.imei,
				deviceLogGridCols.locationAltitude,
				deviceLogGridCols.locationAngle,
				deviceLogGridCols.locationSpeed,
				deviceLogGridCols.ioBluetoothStatus,
				deviceLogGridCols.ioExternalVoltage,
				deviceLogGridCols.ioFuelConsumerLiters,
				deviceLogGridCols.locationGnssHdop,
				deviceLogGridCols.locationGnssPdop,
				deviceLogGridCols.locationGnssStatus,
				deviceLogGridCols.locationGmsActiveOperator,
				deviceLogGridCols.locationGsmSignalAreaCode,
				deviceLogGridCols.locationGsmSignalStrength,
				deviceLogGridCols.ioIccid,
				deviceLogGridCols.isMoving,
				deviceLogGridCols.ioPropertyCount,
				deviceLogGridCols.ioRfidTag,
				deviceLogGridCols.ioSleepMode,
				deviceLogGridCols.ioMileageCountedInMeters,
				deviceLogGridCols.ioTripStatus,
				deviceLogGridCols.ioUmtsLteId,
				deviceLogGridCols.priority,
			];
		case 'FMC130_PRO':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.deviceTimestamp,
				deviceLogGridCols.serverTimestamp,
				deviceLogGridCols.lockState,
				deviceLogGridCols.isIgnitionOn,
				deviceLogGridCols.ioDigitalIn1,
				deviceLogGridCols.ioDigitalIn2,
				deviceLogGridCols.ioDigitalOut1,
				deviceLogGridCols.speedKiloMeters,
				deviceLogGridCols.ioSpeedKiloMetersPerHour,
				deviceLogGridCols.ioMileageInMeters,
				deviceLogGridCols.ioIbuttonCode,
				deviceLogGridCols.ioBatteryPercentage,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.chargingStatus,
				deviceLogGridCols.avlUndefined,
				deviceLogGridCols.canBus1,
				deviceLogGridCols.canBus2,
				deviceLogGridCols.canBus3,
				deviceLogGridCols.fuelLevelPercentage,
				deviceLogGridCols.canProgram,
				deviceLogGridCols.canSecurityStateFlags,
				deviceLogGridCols.batteryRangeMeters,
				deviceLogGridCols.clientId,
				deviceLogGridCols.codecId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.imei,
				deviceLogGridCols.locationAltitude,
				deviceLogGridCols.locationAngle,
				deviceLogGridCols.locationSpeed,
				deviceLogGridCols.ioBluetoothStatus,
				deviceLogGridCols.ioExternalVoltage,
				deviceLogGridCols.ioFuelConsumerLiters,
				deviceLogGridCols.locationGnssHdop,
				deviceLogGridCols.locationGnssPdop,
				deviceLogGridCols.locationGnssStatus,
				deviceLogGridCols.locationGmsActiveOperator,
				deviceLogGridCols.locationGsmSignalAreaCode,
				deviceLogGridCols.locationGsmSignalStrength,
				deviceLogGridCols.ioIccid,
				deviceLogGridCols.isMoving,
				deviceLogGridCols.ioPropertyCount,
				deviceLogGridCols.ioRfidTag,
				deviceLogGridCols.ioSleepMode,
				deviceLogGridCols.ioOdoMeterTotal,
				deviceLogGridCols.ioUmtsLteId,
				deviceLogGridCols.priority,
			];
		case 'FMC125':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.deviceTimestamp,
				deviceLogGridCols.serverTimestamp,
				deviceLogGridCols.lockState,
				deviceLogGridCols.isIgnitionOn,
				deviceLogGridCols.ioDigitalIn1,
				deviceLogGridCols.ioDigitalIn2,
				deviceLogGridCols.ioDigitalOut1,
				deviceLogGridCols.speedKiloMeters,
				deviceLogGridCols.ioSpeedKiloMetersPerHour,
				deviceLogGridCols.ioMileageInMeters,
				deviceLogGridCols.ioIbuttonCode,
				deviceLogGridCols.ioBatteryPercentage,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.chargingStatus,
				deviceLogGridCols.avlUndefined,
				deviceLogGridCols.canBus1,
				deviceLogGridCols.canBus2,
				deviceLogGridCols.canBus3,
				deviceLogGridCols.fuelLevelPercentage,
				deviceLogGridCols.canProgram,
				deviceLogGridCols.canSecurityStateFlags,
				deviceLogGridCols.batteryRangeMeters,
				deviceLogGridCols.clientId,
				deviceLogGridCols.codecId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.imei,
				deviceLogGridCols.locationAltitude,
				deviceLogGridCols.locationAngle,
				deviceLogGridCols.locationSpeed,
				deviceLogGridCols.ioBluetoothStatus,
				deviceLogGridCols.ioExternalVoltage,
				deviceLogGridCols.ioFuelConsumerLiters,
				deviceLogGridCols.locationGnssHdop,
				deviceLogGridCols.locationGnssPdop,
				deviceLogGridCols.locationGnssStatus,
				deviceLogGridCols.locationGmsActiveOperator,
				deviceLogGridCols.locationGsmSignalAreaCode,
				deviceLogGridCols.locationGsmSignalStrength,
				deviceLogGridCols.ioIccid,
				deviceLogGridCols.isMoving,
				deviceLogGridCols.ioPropertyCount,
				deviceLogGridCols.ioRfidTag,
				deviceLogGridCols.ioSleepMode,
				deviceLogGridCols.ioMileageCountedInMeters,
				deviceLogGridCols.ioTripStatus,
				deviceLogGridCols.ioUmtsLteId,
				deviceLogGridCols.priority,
			];
		case 'FMC125_PRO':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.deviceTimestamp,
				deviceLogGridCols.serverTimestamp,
				deviceLogGridCols.lockState,
				deviceLogGridCols.isIgnitionOn,
				deviceLogGridCols.ioDigitalIn1,
				deviceLogGridCols.ioDigitalIn2,
				deviceLogGridCols.ioDigitalOut1,
				deviceLogGridCols.speedKiloMeters,
				deviceLogGridCols.ioSpeedKiloMetersPerHour,
				deviceLogGridCols.ioMileageInMeters,
				deviceLogGridCols.ioIbuttonCode,
				deviceLogGridCols.ioBatteryPercentage,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.chargingStatus,
				deviceLogGridCols.avlUndefined,
				deviceLogGridCols.canBus1,
				deviceLogGridCols.canBus2,
				deviceLogGridCols.canBus3,
				deviceLogGridCols.fuelLevelPercentage,
				deviceLogGridCols.canProgram,
				deviceLogGridCols.canSecurityStateFlags,
				deviceLogGridCols.batteryRangeMeters,
				deviceLogGridCols.clientId,
				deviceLogGridCols.codecId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.imei,
				deviceLogGridCols.locationAltitude,
				deviceLogGridCols.locationAngle,
				deviceLogGridCols.locationSpeed,
				deviceLogGridCols.ioBluetoothStatus,
				deviceLogGridCols.ioExternalVoltage,
				deviceLogGridCols.ioFuelConsumerLiters,
				deviceLogGridCols.locationGnssHdop,
				deviceLogGridCols.locationGnssPdop,
				deviceLogGridCols.locationGnssStatus,
				deviceLogGridCols.locationGmsActiveOperator,
				deviceLogGridCols.locationGsmSignalAreaCode,
				deviceLogGridCols.locationGsmSignalStrength,
				deviceLogGridCols.ioIccid,
				deviceLogGridCols.isMoving,
				deviceLogGridCols.ioPropertyCount,
				deviceLogGridCols.ioRfidTag,
				deviceLogGridCols.ioSleepMode,
				deviceLogGridCols.ioMileageCountedInMeters,
				deviceLogGridCols.ioTripStatus,
				deviceLogGridCols.ioUmtsLteId,
				deviceLogGridCols.priority,
			];
		case 'FMC003':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.deviceTimestamp,
				deviceLogGridCols.serverTimestamp,
				deviceLogGridCols.isIgnitionOn,
				deviceLogGridCols.ioDigitalIn1,
				deviceLogGridCols.ioDigitalIn2,
				deviceLogGridCols.ioDigitalOut1,
				deviceLogGridCols.speedKiloMeters,
				deviceLogGridCols.ioSpeedKiloMetersPerHour,
				deviceLogGridCols.ioOdoMeterTotal,
				deviceLogGridCols.ioBatteryPercentage,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.immobiliser,
				deviceLogGridCols.avlUndefined,
				deviceLogGridCols.clientId,
				deviceLogGridCols.codecId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.imei,
				deviceLogGridCols.locationAltitude,
				deviceLogGridCols.locationAngle,
				deviceLogGridCols.locationSpeed,
				deviceLogGridCols.locationGnssHdop,
				deviceLogGridCols.locationGnssPdop,
				deviceLogGridCols.locationGnssStatus,
				deviceLogGridCols.locationGmsActiveOperator,
				deviceLogGridCols.locationGsmSignalAreaCode,
				deviceLogGridCols.locationGsmSignalStrength,
				deviceLogGridCols.ioIccid,
				deviceLogGridCols.isMoving,
				deviceLogGridCols.ioPropertyCount,
				deviceLogGridCols.ioSleepMode,
				deviceLogGridCols.ioTripOdoMeter,
				deviceLogGridCols.ioTripStatus,
				deviceLogGridCols.ioUmtsLteId,
				deviceLogGridCols.priority,
			];
		case 'SKBIEL01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.wheelTurns,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKBIEL01_PRO':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.wheelTurns,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKBIEL01_PRO_KEYPAD':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.wheelTurns,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKBIEL03':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configNfcSensitivity,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.lockAssetFirmwareVersion,
				deviceLogGridCols.lockAssetId,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.wheelTurns,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKBIEL03_PRO':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.configNfcSensitivity,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.lockAssetFirmwareVersion,
				deviceLogGridCols.lockAssetId,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.wheelTurns,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKTREL02':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.traceId,
			];
		case 'SKTREL03':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.traceId,
			];
		case 'SKTREL03_PRO':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.traceId,
			];
		case 'SKDOEL01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configNfcSensitivity,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.accessCorrelationId,
				deviceLogGridCols.accessKeyId,
				deviceLogGridCols.accessRuleId,
				deviceLogGridCols.accessState,
				deviceLogGridCols.accessType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.lockAssetFirmwareVersion,
				deviceLogGridCols.lockAssetId,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalAccessCalls,
				deviceLogGridCols.statsTotalAccessError,
				deviceLogGridCols.statsTotalAccessFailed,
				deviceLogGridCols.statsTotalAccessSuccess,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalReconnects,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKDOEL02':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configNfcSensitivity,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.accessCorrelationId,
				deviceLogGridCols.accessKeyId,
				deviceLogGridCols.accessRuleId,
				deviceLogGridCols.accessState,
				deviceLogGridCols.accessType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.lockAssetFirmwareVersion,
				deviceLogGridCols.lockAssetId,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalAccessCalls,
				deviceLogGridCols.statsTotalAccessError,
				deviceLogGridCols.statsTotalAccessFailed,
				deviceLogGridCols.statsTotalAccessSuccess,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalReconnects,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKDOEL03':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configNfcSensitivity,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.accessCorrelationId,
				deviceLogGridCols.accessKeyId,
				deviceLogGridCols.accessRuleId,
				deviceLogGridCols.accessState,
				deviceLogGridCols.accessType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.lockAssetFirmwareVersion,
				deviceLogGridCols.lockAssetId,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalAccessCalls,
				deviceLogGridCols.statsTotalAccessError,
				deviceLogGridCols.statsTotalAccessFailed,
				deviceLogGridCols.statsTotalAccessSuccess,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalReconnects,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKDOEL04':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.lockState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configNfcSensitivity,
				deviceLogGridCols.configNfcTuning,
				deviceLogGridCols.accessCorrelationId,
				deviceLogGridCols.accessKeyId,
				deviceLogGridCols.accessRuleId,
				deviceLogGridCols.accessState,
				deviceLogGridCols.accessType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.lockAssetFirmwareVersion,
				deviceLogGridCols.lockAssetId,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalAccessCalls,
				deviceLogGridCols.statsTotalAccessError,
				deviceLogGridCols.statsTotalAccessFailed,
				deviceLogGridCols.statsTotalAccessSuccess,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalReconnects,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKBR01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.borderRouterHostName,
				deviceLogGridCols.borderRouterIfMode,
				deviceLogGridCols.borderRouterIpAddress,
				deviceLogGridCols.borderRouterThreadMode,
				deviceLogGridCols.borderRouterTxPower,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.traceId,
			];
		case 'SKBR02':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.borderRouterHostName,
				deviceLogGridCols.borderRouterIfMode,
				deviceLogGridCols.borderRouterIpAddress,
				deviceLogGridCols.borderRouterThreadMode,
				deviceLogGridCols.borderRouterTxPower,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.traceId,
			];
		case 'SKBR02_CTRL':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.borderRouterHostName,
				deviceLogGridCols.borderRouterIfMode,
				deviceLogGridCols.borderRouterIpAddress,
				deviceLogGridCols.borderRouterThreadMode,
				deviceLogGridCols.borderRouterTxPower,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.traceId,
			];
		case 'SKINCS01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.traceId,
			];
		case 'SKRT01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalReconnects,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.traceId,
			];
		case 'SKHANG01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.keyTokenId,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.accessKeyId,
				deviceLogGridCols.accessRuleId,
				deviceLogGridCols.accessState,
				deviceLogGridCols.accessType,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.configOfflineKeyCount,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.keySerialNumber,
				deviceLogGridCols.keyTagType,
				deviceLogGridCols.traceId,
			];
		case 'SKVOK01':
			return [
				deviceLogGridCols.id,
				deviceLogGridCols.timeStamp,
				deviceLogGridCols.deviceState,
				deviceLogGridCols.firmwareVersion,
				deviceLogGridCols.batteryPercentage,
				deviceLogGridCols.temperature,
				deviceLogGridCols.rssi,
				deviceLogGridCols.location,
				deviceLogGridCols.locationSatellitesSeen,
				deviceLogGridCols.locationSatellitesUsed,
				deviceLogGridCols.eventType,
				deviceLogGridCols.eventCode,
				deviceLogGridCols.debug,
				deviceLogGridCols.proxyId,
				deviceLogGridCols.proxyTimeZone,
				deviceLogGridCols.proxyType,
				deviceLogGridCols.configConnectivityProfile,
				deviceLogGridCols.clientId,
				deviceLogGridCols.chargerState,
				deviceLogGridCols.hardwareId,
				deviceLogGridCols.deviceId,
				deviceLogGridCols.locationResult,
				deviceLogGridCols.locationTtf,
				deviceLogGridCols.modemCellId,
				deviceLogGridCols.modemCellLac,
				deviceLogGridCols.modemCellMcc,
				deviceLogGridCols.modemCellMnc,
				deviceLogGridCols.modemCellRsrp,
				deviceLogGridCols.modemCoprocessorFirmwareVersion,
				deviceLogGridCols.modemFirmwareVersion,
				deviceLogGridCols.modemImei,
				deviceLogGridCols.modemSimIccid,
				deviceLogGridCols.modemSimImsi,
				deviceLogGridCols.statsTotalReboots,
				deviceLogGridCols.statsTotalUnlocks,
				deviceLogGridCols.statusCode,
				deviceLogGridCols.traceId,
			];
		default:
			return [];
	}
};

export default DeviceModel;

/**
 * Little snipper for parsing the fields from swagger
 * 
 * for (var i = 0; i< fields.length ; i++) {
  let updatedField = fields[i].propertyName
  
  while (updatedField.includes('.') === true) {
    const index = updatedField.indexOf('.')
    updatedField = updatedField.replace('.', '')
    updatedField = updatedField.split('')
    updatedField[index] = updatedField[index].toUpperCase()
    updatedField = updatedField.join('')
  }
  
  console.log(`deviceLogGridCols.${updatedField},`)
}
 */
